import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { useServices } from "@core/servicesContext";
import { CurrentProfile } from "@domain/currentProfile/currentProfile";
import { Group } from "@domain/group/group";
import { useKyc } from "@domain/userKyc/hook";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Description } from "@ui/components/containers/description";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { DashboardDealing } from "@ui/components/group/shareroom/dashboard/dashboardDealing";
import { DashboardHome } from "@ui/components/group/shareroom/dashboard/dashboardHome";
import { DashboardIcon, DealingIcon, MenuIcon, SettingIcon } from "@ui/components/icons";
import { Stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MonetizationRuleModal } from "@ui/components/profile/monetization/monetizationRuleModal";
import { VerticalTabView } from "@ui/components/tabViews";
import { UserKycEditionModal } from "@ui/components/userKyc/userKycEditionModal";
import { useModalTools } from "@ui/modals/modalContext";
import { alpha, gap, mobile, mobileOnly } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { SettingEditor } from "@ui/pages/group/shareroom/dashboard/settingEditorPage/settingEditor";

interface DashboardPageProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	currentProfile: CurrentProfile | null;
}

const tabs =
	Environment.APP_VARIANT === "jaangal"
		? ([
				{ label: "GENERAL", Icon: <DashboardIcon /> },
				{ label: "SETTING", Icon: <SettingIcon /> },
			] as const)
		: ([
				{ label: "GENERAL", Icon: <DashboardIcon /> },
				{ label: "DEALING", Icon: <DealingIcon /> },
				{ label: "SETTING", Icon: <SettingIcon /> },
			] as const);

export const DashboardPage: React.FC<DashboardPageProps> = ({ shareroom, currentProfile, ...htmlProps }) => {
	const { trackingService } = useServices();
	const isMobile = useIsMobile();
	const [tabIndex, setTabIndex] = useState<number>(0);
	const currentTab = tabs[tabIndex]["label"];
	const { result: kyc } = useKyc();

	const { createModal } = useModalTools();

	const isMonetizationTermOk = currentProfile?.monetizationTermsConsented ?? false;

	const step: "RULE_ACCEPTED" | "ADD_KYC" = !isMonetizationTermOk ? "RULE_ACCEPTED" : "ADD_KYC";

	const openMonetizationRuleModal = (): void => {
		createModal((close) => (
			<MonetizationRuleModal
				onKycChange={(userKyc) => {
					if (userKyc) {
						setTabIndex(tabs.findIndex((item) => item.label === "SETTING"));
					}
				}}
				onMonetizationTermsChange={(onKycChange) =>
					onKycChange((userKyc) => {
						if (userKyc) {
							setTabIndex(tabs.findIndex((item) => item.label === "SETTING"));
						}
					})
				}
				close={close}
			/>
		));
	};

	const openKycEditionModal = (): void => {
		createModal((close) => (
			<UserKycEditionModal
				onKycChange={(userKyc) => {
					if (userKyc) setTabIndex(tabs.findIndex((item) => item.label === "SETTING"));
				}}
				close={close}
			/>
		));
	};

	return (
		<Container {...htmlProps}>
			{!isMobile ? (
				<LeftMenu>
					<VerticalTabView
						items={tabs}
						labelKey="shareroom.dashboard.menu.title"
						tabIndex={tabIndex}
						onClick={(index) => {
							if (config.features.classrooms.enablePremium) {
								if (index >= 1 && (!isMonetizationTermOk || !kyc)) return;
								if (index === 1 && shareroom.license === "FREE") return;
							}
							setTabIndex(index);
						}}
					/>
				</LeftMenu>
			) : null}
			{currentTab === "GENERAL" ? (
				<HomeContentBlock>
					{(!shareroom.license || shareroom.license === "FREE") && Environment.APP_VARIANT !== "jaangal" ? (
						<MonetizationBloc>
							<Stack gap={25} align="center">
								<Description>{I18n.t("shareroom.dashboard.general.monetize.description")}</Description>
								<PrimaryButton
									onClick={() => {
										trackingService.track("Monetize my classroom_CTA");
										if (isMonetizationTermOk && kyc) {
											setTabIndex(tabs.findIndex((item) => item.label === "SETTING"));
											return;
										}

										switch (step) {
											case "RULE_ACCEPTED":
												openMonetizationRuleModal();
												break;
											case "ADD_KYC":
												openKycEditionModal();
												break;
											default:
												break;
										}
									}}
								>
									{I18n.t("shareroom.dashboard.general.monetize.button.label")}
								</PrimaryButton>
							</Stack>
						</MonetizationBloc>
					) : null}
					<DashboardHome shareroom={shareroom} />
				</HomeContentBlock>
			) : currentTab === "DEALING" ? (
				<HomeContentBlock>
					<DashboardDealing shareroom={shareroom} />
				</HomeContentBlock>
			) : currentTab === "SETTING" ? (
				<ContentBlock>
					<SettingEditor initialShareroom={shareroom} />
				</ContentBlock>
			) : null}

			{isMobile ? (
				<MobileMenuButton Icon={<MenuIcon />} closeOnSelect>
					<VerticalTabView
						items={tabs}
						labelKey="shareroom.dashboard.menu.title"
						tabIndex={tabIndex}
						onClick={(index) => {
							if (config.features.classrooms.enablePremium) {
								if (index >= 1 && (!isMonetizationTermOk || !kyc)) return;
								if (index === 1 && shareroom.license === "FREE") return;
								setTabIndex(index);
							}
						}}
					/>
				</MobileMenuButton>
			) : null}
		</Container>
	);
};

const Container = styled.div`
	display: grid;
	grid-template-columns: 280px 1fr;
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-template-areas:
		"menu content"
		". content"
		". content"
		". content";

	@media ${mobile} {
		display: block;
	}
`;

const LeftMenu = styled(RoundedBlock)`
	grid-area: menu;
	padding: 40px 31px 40px 36px;
	${gap(38)};
`;

const HomeContentBlock = styled.div`
	grid-area: content;
	${gap(25)};

	/* @media ${mobile} {
		padding: 40px 20px 80px;
	} */
`;

const ContentBlock = styled(RoundedBlock)`
	padding: 60px 57px;
	grid-area: content;
	${gap(40)};

	/* @media ${mobile} {
		padding: 40px 20px 80px;
	} */
`;

const MobileMenuButton = styled(KebabMenu)`
	${mobileOnly("flex")};
	pointer-events: auto;
	position: fixed;
	bottom: 28px;
	right: 20px;
	justify-content: center;
	align-items: center;
	width: 50px;
	height: 50px;
	border-radius: 25px;
	background-color: ${({ theme }) => theme.colors.primary};
	box-shadow: 0 2px 14px 0 ${({ theme }) => alpha(theme.colors.primary, 0.6)};
	z-index: ${Z.menu};
`;

const MonetizationBloc = styled(RoundedBlock)`
	padding: 60px 57px;
`;
